

import React, { useState, Suspense, useEffect } from 'react';
import { lazyPage } from './services/lazyPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionWaitScreen } from './components/SessionWaitScreen';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp, useAppSelector } from './context/AppContext';

/* v8 ignore start -- test-only bridge active only when VITE_USE_EMULATOR=true */
const E2EBridge: React.FC = () => {
  const app = useApp();
  useEffect(() => {
    (window as any).__e2e__ = app;
  }, [app]);
  return null;
};
const isE2E = import.meta.env.VITE_USE_EMULATOR === 'true';
/* v8 ignore stop */
import { Sidebar } from './components/Sidebar';
import { MobileBottomTabs } from './components/MobileBottomTabs';
import { NotificationProvider, useNotifications } from './components/NotificationHandler';
import { NotificationBell, NotificationPanel, FloatingNotificationBell } from './components/NotificationCenter';
import { CustomerReplyAlerts } from './components/CustomerReplyAlerts';
import { TaskNotificationListener } from './components/TaskNotificationListener';
import { TASK_BOARD_ROLES } from './services/tasks';
import { FEATURES } from './services/features';
import { NotificationPermissionBanner } from './components/NotificationPermissionBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CustomersRoute } from './components/CustomersRoute';
import { ToastContainer } from './components/ToastContainer';
import { UserRole } from './types';
import { defaultPathForRole } from './services/roleHome';
import { Loader2, Menu } from 'lucide-react';
import { PageSkeleton } from './components/PageSkeleton';

// Lazy-loaded pages (code-split into separate chunks)
const Dashboard = lazyPage(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Products = lazyPage(() => import('./pages/Products').then(m => ({ default: m.Products })));
const InventoryDispatch = lazyPage(() => import('./pages/InventoryDispatch').then(m => ({ default: m.InventoryDispatch })));
const StoreHub = lazyPage(() => import('./pages/StoreHub').then(m => ({ default: m.StoreHub })));
const DriverApp = lazyPage(() => import('./pages/DriverApp').then(m => ({ default: m.DriverApp })));
const OrderManagement = lazyPage(() => import('./pages/OrderManagement').then(m => ({ default: m.OrderManagement })));
const PaymentApproval = lazyPage(() => import('./pages/PaymentApproval').then(m => ({ default: m.PaymentApproval })));
const Payments = lazyPage(() => import('./pages/Payments').then(m => ({ default: m.Payments })));
const AdminPanel = lazyPage(() => import('./pages/AdminPanel').then(m => ({ default: m.AdminPanel })));
const StoreStockSearch = lazyPage(() => import('./pages/StoreStockSearch').then(m => ({ default: m.StoreStockSearch })));
const StoreReports = lazyPage(() => import('./pages/StoreReports').then(m => ({ default: m.StoreReports })));
const AdminReports = lazyPage(() => import('./pages/AdminReports').then(m => ({ default: m.AdminReports })));
const WarehouseHistory = lazyPage(() => import('./pages/WarehouseHistory').then(m => ({ default: m.WarehouseHistory })));
const FullHistory = lazyPage(() => import('./pages/FullHistory').then(m => ({ default: m.FullHistory })));
const DriverReports = lazyPage(() => import('./pages/DriverReports').then(m => ({ default: m.DriverReports })));
const WarehouseReports = lazyPage(() => import('./pages/WarehouseReports').then(m => ({ default: m.WarehouseReports })));
const StoreHandoffReport = lazyPage(() => import('./pages/StoreHandoffReport').then(m => ({ default: m.StoreHandoffReport })));
const Messages = lazyPage(() => import('./pages/Messages').then(m => ({ default: m.Messages })));
const AgentConsole = lazyPage(() => import('./pages/AgentConsole').then(m => ({ default: m.AgentConsole })));
const EmailInbox = lazyPage(() => import('./pages/EmailInbox').then(m => ({ default: m.EmailInbox })));
const DispatchOversight = lazyPage(() => import('./pages/DispatchOversight').then(m => ({ default: m.DispatchOversight })));
const DriverControl = lazyPage(() => import('./pages/DriverControl').then(m => ({ default: m.DriverControl })));
const TaskBoard = lazyPage(() => import('./pages/TaskBoard').then(m => ({ default: m.TaskBoard })));
const ForwardedInbox = lazyPage(() => import('./pages/ForwardedInbox').then(m => ({ default: m.ForwardedInbox })));
const UploadPage = lazyPage(() => import('./pages/UploadPage').then(m => ({ default: m.UploadPage })));
const PaymentProofPage = lazyPage(() => import('./pages/PaymentProofPage'));
const Login = lazyPage(() => import('./pages/Login').then(m => ({ default: m.Login })));
const ForcePasswordChange = lazyPage(() => import('./pages/ForcePasswordChange').then(m => ({ default: m.ForcePasswordChange })));
const Settings = lazyPage(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Customers = lazyPage(() => import('./pages/Customers').then(m => ({ default: m.Customers })));
const IdVerification = lazyPage(() => import('./pages/IdVerification').then(m => ({ default: m.IdVerification })));
const InventoryTracker = lazyPage(() => import('./pages/InventoryTracker').then(m => ({ default: m.InventoryTracker })));
const StockOverview = lazyPage(() => import('./pages/StockOverview').then(m => ({ default: m.StockOverview })));
const DeliveryMap = lazyPage(() => import('./pages/DeliveryMap').then(m => ({ default: m.DeliveryMap })));
const ShippingTest = lazyPage(() => import('./pages/ShippingTest').then(m => ({ default: m.ShippingTest })));
const EmailTemplates = lazyPage(() => import('./pages/EmailTemplates').then(m => ({ default: m.EmailTemplates })));
const WarehouseManagerHub = lazyPage(() => import('./pages/WarehouseManagerHub').then(m => ({ default: m.WarehouseManagerHub })));
const WorkerHub = lazyPage(() => import('./pages/WorkerHub').then(m => ({ default: m.WorkerHub })));
const WorkerReport = lazyPage(() => import('./pages/WorkerReport').then(m => ({ default: m.WorkerReport })));
const PackagerHub = lazyPage(() => import('./pages/PackagerHub').then(m => ({ default: m.PackagerHub })));
const PackagerReport = lazyPage(() => import('./pages/PackagerReport').then(m => ({ default: m.PackagerReport })));

const PageLoader = () => (
  <div className="flex items-center justify-center p-12">
    <Loader2 className="animate-spin text-brand-500" size={32} />
  </div>
);

const AppContent: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Subscribe to just these two slices so the whole app shell/layout stops
  // re-rendering on every unrelated Firestore snapshot (the global churn).
  const currentUser = useAppSelector(s => s.currentUser);
  const loading = useAppSelector(s => s.loading);
  const sessionPending = useAppSelector(s => s.sessionPending);
  const retrySession = useAppSelector(s => s.retrySession);
  const logout = useAppSelector(s => s.logout);
  const location = useLocation();
  // Chat pages (Messages) must lock the viewport — list + chat each scroll
  // independently inside their column, page itself never scrolls. Other pages
  // keep the default min-h-full so long tables/lists can grow inside <main>.
  const isViewportLockedPage = location.pathname.startsWith('/messages') || location.pathname.startsWith('/agent_console') || location.pathname.startsWith('/inbox') || location.pathname.startsWith('/text_inbox') || location.pathname.startsWith('/payment_approval') || location.pathname.startsWith('/tasks');
  // Full-bleed pages drop the max-w-7xl reading column — the payment kanban
  // needs every pixel of width for its columns to scroll horizontally, and the
  // Text Inbox is a three-column chat whose panes must reach both edges (the
  // centered column left a dead gap beside the sidebar on wide/zoomed-out
  // screens and could overflow horizontally with the contact panel docked).
  // The Tasks board is a kanban too (2026-09-24): locked height so each
  // column scrolls on its own, full width for the columns.
  const isFullBleedPage = location.pathname.startsWith('/payment_approval') || location.pathname.startsWith('/text_inbox') || location.pathname.startsWith('/tasks');
  // Call ALL hooks unconditionally before any early returns — rules of hooks.
  // Transitioning between loading → logged in changed the hook count below,
  // which made React crash the render (white page after login).
  const { flashActive, dismissFlash } = useNotifications();

  // Public customer photo-upload page (/u/:token) — no login, opened from an SMS
  // link on a phone. Rendered before any auth/loading gate so a customer never
  // hits the login screen.
  // Public "Already paid?" form (/pay/:token, 0146) — the link in an e-Transfer order's emails. Same rule: no login.
  // (/pay exactly — the websites' link, ?order= — not /payments, the staff page.)
  if (location.pathname.startsWith('/pay/') || location.pathname === '/pay') {
    return (
      <>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/pay" element={<PaymentProofPage />} />
            <Route path="/pay/:token" element={<PaymentProofPage />} />
          </Routes>
        </Suspense>
      </>
    );
  }

  if (location.pathname.startsWith('/u/')) {
    return (
      <>
        <ToastContainer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/u/:token" element={<UploadPage />} />
          </Routes>
        </Suspense>
      </>
    );
  }

  // Show loading spinner while Firebase initializes
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-slate-900">
        <Loader2 className="animate-spin text-brand-500 mb-4" size={48} />
        <p className="text-slate-400 font-medium animate-pulse">Initializing System...</p>
      </div>
    );
  }

  // Signed in, but the profile has not loaded yet. The session is alive, so a password would
  // change nothing — wait and retry instead of showing the login page (incident 2026-09-18..20).
  // "Signing in…" first; "Reconnecting…" only if the read is slow (components/SessionWaitScreen).
  if (!currentUser && sessionPending) {
    return <SessionWaitScreen onRetry={retrySession} onSignOut={() => logout()} />;
  }

  // If not logged in, show login page
  if (!currentUser) {
    return (
      <>
        <ToastContainer />
        <Suspense fallback={<PageLoader />}><Login /></Suspense>
      </>
    );
  }

  // Guard: If password reset is required, force that screen
  if (currentUser.requiresPasswordReset) {
    return (
      <>
        <ToastContainer />
        <Suspense fallback={<PageLoader />}><ForcePasswordChange /></Suspense>
      </>
    );
  }

  const defaultPath = defaultPathForRole(currentUser.role);

  return (
    // App shell: bounded 100dvh flex container. 100dvh shrinks when the
    // mobile keyboard opens so flex children resize (WhatsApp-style) — this is
    // the key to everything staying visible without overlap.
    <div className="flex bg-slate-50 h-[100dvh] relative overflow-hidden">
      {/* v8 ignore next -- test-only bridge */}
      {isE2E && <E2EBridge />}
      {/* Red flash overlay for new notifications */}
      <div
        onClick={() => { dismissFlash(); }}
        // A SCROLL gesture has to clear it too. The overlay is
        // `pointer-events-auto` while it is up and it has no timeout, so until
        // this was added every wheel tick (and every touch drag) landed on the
        // overlay instead of the page: the chat list simply did not move while
        // notifications kept arriving, and then lurched as soon as something
        // was finally clicked. That is half of the "the list jumps like someone
        // pushes it" report — the other half was the message list's own
        // smooth-follow. Dismissing on the first wheel costs that one tick and
        // gives the page back.
        onWheel={() => { dismissFlash(); }}
        onTouchMove={() => { dismissFlash(); }}
        onPointerDown={(e) => {
          dismissFlash();
          const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
          if (el && el !== e.currentTarget) {
            el.click();
          }
        }}
        className={`fixed inset-0 z-[9999] transition-all duration-500 ${flashActive ? 'opacity-100 pointer-events-auto cursor-pointer' : 'opacity-0 pointer-events-none'}`}
        style={{
          background: 'rgba(220,38,38,0.15)',
          boxShadow: 'inset 0 0 100px 40px rgba(220,38,38,0.35)',
          border: '5px solid rgba(220,38,38,0.7)',
          borderRadius: '0px',
        }}
      />
      <ToastContainer />
      {/* The notification panel is a fixed overlay; it renders itself only when
          open (see NotificationPanel). Mounting it once at the shell root means
          both the mobile-header bell and the desktop floating bell open the
          same dropdown. */}
      <NotificationPanel />

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Permission nudge (shrink-0 so it doesn't eat scroll area) */}
        <NotificationPermissionBanner />
        {/* Mobile header — shrink-0 so it keeps its natural height in the flex column.
            The bell used to be a plain shortcut to /messages; it is now the trigger
            for the persistent notification panel (see components/NotificationCenter),
            so no alert is ever lost when its 5-second toast disappears. */}
        <div className="md:hidden bg-slate-900 text-white p-4 flex items-center justify-between z-40 shadow-md shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-slate-800 rounded">
              <Menu size={24} />
            </button>
            <span className="font-bold text-lg">WolfDrivers</span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell variant="dark" />
            <div className="w-8 h-8 bg-brand-600 rounded-full flex items-center justify-center font-bold text-xs">
              {currentUser.name.charAt(0)}
            </div>
          </div>
        </div>

        {/* Desktop bell — no header on desktop, so the bell floats top-right of the
            main area, and can be dragged anywhere (the place is remembered per browser,
            2026-10-03). The panel itself lives once (below) and both bells drive the
            same open state. */}
        <FloatingNotificationBell />

        {/* Scrollable main area — flex-1 gives it exactly the remaining space.
            On chat pages we switch to overflow-hidden + h-full so the page
            can't scroll and inner panels resolve a definite height. */}
        <main className={`flex-1 min-h-0 ${isViewportLockedPage ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className={`${isFullBleedPage ? 'max-w-none' : 'max-w-7xl'} mx-auto w-full ${isViewportLockedPage ? 'h-full' : 'min-h-full'}`}>
            {/* Route chunks take 0.3–1.5 s to arrive on the live site; a layout
                skeleton keeps the frame stable instead of a lone spinner. */}
            <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageSkeleton />}>
              <Routes>
              <Route path="/" element={<Navigate to={defaultPath} replace />} />

              <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin_reports" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <AdminReports />
                </ProtectedRoute>
              } />
              <Route path="/products" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STOCK_MANAGER, UserRole.MANAGER]}>
                  <Products />
                </ProtectedRoute>
              } />
              <Route path="/warehouse_history" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <WarehouseHistory />
                </ProtectedRoute>
              } />
              <Route path="/full_history" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <FullHistory />
                </ProtectedRoute>
              } />
              <Route path="/inventory" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <InventoryDispatch />
                </ProtectedRoute>
              } />
              <Route path="/inventory_tracker" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <InventoryTracker />
                </ProtectedRoute>
              } />
              <Route path="/stock_overview" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.STOCK_MANAGER, UserRole.MANAGER]}>
                  <StockOverview />
                </ProtectedRoute>
              } />
              <Route path="/store_stock_search" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <StoreStockSearch />
                </ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <OrderManagement />
                </ProtectedRoute>
              } />
              {/* Email Templates (2026-10-30): admins edit the customer emails — the admin tier writes settings (RLS). */}
              {FEATURES.orderBoard && <Route path="/email_templates" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <EmailTemplates />
                </ProtectedRoute>
              } />}
              {FEATURES.orderBoard && <Route path="/payment_approval" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <PaymentApproval />
                </ProtectedRoute>
              } />}
              {/* E-transfer inbox (0116): the roles a super admin may grant; the page itself checks the grant. */}
              {FEATURES.orderBoard && <Route path="/payments" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <Payments />
                </ProtectedRoute>
              } />}
              {/* Team task board (0091) — roles per services/tasks TASK_BOARD_ROLES. */}
              {FEATURES.taskBoard && <Route path="/tasks" element={
                <ProtectedRoute allowedRoles={[...TASK_BOARD_ROLES]}>
                  <TaskBoard />
                </ProtectedRoute>
              } />}
              <Route path="/delivery_map" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <DeliveryMap />
                </ProtectedRoute>
              } />
              {/* Canada Post bench — verify pickup/drop-off, rate the parcel,
                  then create the label for the chosen service. */}
              {FEATURES.shipping && <Route path="/shipping_labels" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <ShippingTest />
                </ProtectedRoute>
              } />}
              {/* the old address, kept for bookmarks */}
              {FEATURES.shipping && <Route path="/shipping_test" element={<Navigate to="/shipping_labels" replace />} />}
              <Route path="/store_reports" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <StoreReports />
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <AdminPanel />
                </ProtectedRoute>
              } />
              <Route path="/customers" element={
                <CustomersRoute>
                  {(fullList) => <Customers fullList={fullList} />}
                </CustomersRoute>
              } />
              {/* ID verification (0126): one customer per email + phone pair, with the ID check. Super admin only. */}
              {FEATURES.idVerification && <Route path="/id_verification" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN]}>
                  <IdVerification />
                </ProtectedRoute>
              } />}
              {/* The website ID check's review queue (0128): only pairs waiting for a person, for the dispatch tier and
                  the super admin (who also has the whole section at /id_verification; 2026-10-14). */}
              {FEATURES.idVerification && <Route path="/id_reviews" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <IdVerification reviewsOnly />
                </ProtectedRoute>
              } />}
              <Route path="/store_hub" element={
                <ProtectedRoute allowedRoles={[UserRole.STORE]}>
                  <StoreHub />
                </ProtectedRoute>
              } />
              <Route path="/driver_app" element={
                <ProtectedRoute allowedRoles={[UserRole.DRIVER]}>
                  <DriverApp />
                </ProtectedRoute>
              } />
              <Route path="/warehouse_manager" element={
                <ProtectedRoute allowedRoles={[UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <WarehouseManagerHub />
                </ProtectedRoute>
              } />
              <Route path="/worker" element={
                <ProtectedRoute allowedRoles={[UserRole.WORKER, UserRole.PACKAGER, UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <WorkerHub />
                </ProtectedRoute>
              } />
              <Route path="/worker_report" element={
                <ProtectedRoute allowedRoles={[UserRole.WORKER, UserRole.PACKAGER, UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <WorkerReport />
                </ProtectedRoute>
              } />
              <Route path="/packager" element={
                <ProtectedRoute allowedRoles={[UserRole.PACKAGER, UserRole.WORKER, UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <PackagerHub />
                </ProtectedRoute>
              } />
              <Route path="/packager_report" element={
                <ProtectedRoute allowedRoles={[UserRole.PACKAGER, UserRole.WORKER, UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER]}>
                  <PackagerReport />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STORE, UserRole.DRIVER, UserRole.WAREHOUSE_MANAGER, UserRole.WORKER, UserRole.PACKAGER, UserRole.STOCK_MANAGER, UserRole.MANAGER, UserRole.TEAM_MEMBER]}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/driver_reports" element={
                <ProtectedRoute allowedRoles={[UserRole.DRIVER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <DriverReports />
                </ProtectedRoute>
              } />
              <Route path="/warehouse_reports" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.WAREHOUSE_MANAGER, UserRole.MANAGER]}>
                  <WarehouseReports />
                </ProtectedRoute>
              } />
              <Route path="/store_handoff" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER]}>
                  <StoreHandoffReport />
                </ProtectedRoute>
              } />
              {/* Warehouse-pipeline roles (WM/worker/packager) have no chat —
                  removed 2026-08-28 per the owner. */}
              <Route path="/messages" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STORE, UserRole.DRIVER]}>
                  <Messages />
                </ProtectedRoute>
              } />
              {FEATURES.agentConsole && <Route path="/agent_console" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <AgentConsole />
                </ProtectedRoute>
              } />}
              {/* /inbox/:msgId deep-links one email — share the URL and it
                  opens that exact conversation at that exact message. */}
              {FEATURES.emailInbox && <Route path="/inbox/:msgId?" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <EmailInbox />
                </ProtectedRoute>
              } />}
              {/* Oversight of the email queue + orders. Admin/super-admin see the
                  whole team; a dispatcher sees only their own emails, orders and
                  reply times (personal view). */}
              {FEATURES.dispatchOversight && <Route path="/dispatch_oversight" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <DispatchOversight />
                </ProtectedRoute>
              } />}
              {/* Driver Control (UPE-119): GPS rule, out-of-shift, who is working
                  today. Admin + super admin only. */}
              <Route path="/driver_control" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                  <DriverControl />
                </ProtectedRoute>
              } />
              {/* Forwarded SMS from the SIM-phone forwarder app — new-contact
                  texts that have no order yet, grouped per number with reply. */}
              <Route path="/text_inbox" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <ForwardedInbox />
                </ProtectedRoute>
              } />
              {/* Selecting a chat navigates to /text_inbox/<number> — without
                  this param route the catch-all bounces the user to their
                  dashboard the moment they open a conversation. */}
              <Route path="/text_inbox/:number" element={
                <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH]}>
                  <ForwardedInbox />
                </ProtectedRoute>
              } />
              {/* Catch-all: redirect unknown paths to role default */}
              <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </div>
        </main>

        {/* Mobile bottom tab bar — flex child so keyboard resizes work (hidden on md+) */}
        <MobileBottomTabs />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppProvider>
        <NotificationProvider>
          <AppContent />
          {FEATURES.customerReplyAlerts && <CustomerReplyAlerts />}
          {FEATURES.taskBoard && <TaskNotificationListener />}
        </NotificationProvider>
      </AppProvider>
    </BrowserRouter>
  );
};

export default App;
